import React, { useMemo, useRef, useState } from 'react';
import RowActionsMenu from '../../shared/RowActionsMenu';
import Icon from '../../shared/Icon';
import { isThreadShared } from '../sharedQueries';

const byActivity = (a, b) => new Date(b.last_activity_at || b.updated_at || 0) - new Date(a.last_activity_at || a.updated_at || 0);

/**
 * PRD-1621: a query's menu. Options sort alphabetically, destructive ones last (also
 * alphabetical); a changing label (Pin / Unpin) sorts as shown. A participant in a shared
 * query can only pin it: pinning is theirs alone, and they can't remove or hide it.
 */
export function queryMenuItems({ thread, isOwner, sharingEnabled, onTogglePin, onRename, onShare, onStopSharing, onDelete }) {
  const items = [
    { key: 'pin', label: thread.pinned ? 'Unpin' : 'Pin', icon: thread.pinned ? 'push-pin-slash' : 'push-pin', onSelect: () => onTogglePin(thread) },
  ];
  if (!isOwner) return items;
  items.push({ key: 'rename', label: 'Rename', icon: 'edit', onSelect: () => onRename(thread) });
  if (sharingEnabled && onShare && onStopSharing) {
    if (isThreadShared(thread)) {
      items.push({ key: 'stop-sharing', label: 'Stop sharing', icon: 'user-minus', onSelect: () => onStopSharing(thread) });
    } else if (!thread.report_automation_name) {
      // Report conversations can't be shared (the server answers 409).
      items.push({ key: 'share', label: 'Share', icon: 'user-plus', onSelect: () => onShare(thread) });
    }
  }
  items.push({ key: 'delete', label: 'Delete', icon: 'trash-simple', danger: true, onSelect: () => onDelete(thread) });
  return items.sort((a, b) => (Number(Boolean(a.danger)) - Number(Boolean(b.danger))) || a.label.localeCompare(b.label));
}

// A History row's trigger is an Icon trigger (28); the thread header's is an Icon Button - Small -
// Tertiary (36 circle) named "Query options". Both carry a 20 glyph, Bold as three dots are at any
// size. The menu sits 8 below its trigger and 16 inside the window (Context Menu).
export function QueryMenu({ trigger = 'row', ...props }) {
  const items = queryMenuItems(props);
  const header = trigger === 'header';
  return (
    <RowActionsMenu
      ariaLabel={header ? 'Query options' : `Options for ${props.thread.title || 'query'}`}
      triggerTip={header ? 'Query options' : undefined}
      triggerClassName={header ? 'iter1-header-trigger' : 'iter1-row-trigger'}
      menuClassName="iter1-menu"
      iconSize={20}
      iconWeight="bold"
      gap={8}
      edge={16}
    >
      {(close) => items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={item.danger ? 'row-actions-menu-danger' : undefined}
          onClick={() => { close({ restoreFocus: true }); item.onSelect(); }}
        >
          <Icon name={item.icon} size={20} /> {item.label}
        </button>
      ))}
    </RowActionsMenu>
  );
}

// The shared glyph's tooltip, and the words the row's name adds for it. A row someone else
// owns is in the list only because it's shared with this person.
function sharedNote(thread, { isOwner, sharingEnabled }) {
  if (!sharingEnabled || thread.locked) return null;
  if (!isOwner) return `Shared by ${thread.owner_name || 'a colleague'}`;
  return isThreadShared(thread) ? 'Shared with your organization' : null;
}

// A shared query whose data you can't access (R11) is listed only by its owner: its name and
// datasets are never shown. Your own keeps its name and menu after you lose access (story 10).
const NO_ACCESS_TIP = 'You don\u2019t have access to the data in this query. Contact your administrator for access';
const OWNER_NO_ACCESS_TIP = 'You no longer have access to the data in this query. Contact your administrator for access';

export const rowLabel = (thread, isOwner) => (thread.locked && !isOwner
  ? `Shared query from ${thread.owner_name || 'a colleague'}`
  : thread.title || 'Untitled conversation');

function QueryRow({ thread, active, onSelect, menuProps, rowRef }) {
  const noAccess = Boolean(thread.locked);
  const title = rowLabel(thread, menuProps.isOwner);
  const shared = sharedNote(thread, menuProps);
  return (
    <li ref={(el) => rowRef?.(thread.id, el)} className={`iter1-row focus-row${active ? ' iter1-row--active' : ''}${noAccess ? ' iter1-row--no-access' : ''}`}>
      <button
        type="button"
        className="iter1-row-main"
        aria-current={active ? 'page' : undefined}
        aria-disabled={noAccess || undefined}
        data-tip={noAccess ? (menuProps.isOwner ? OWNER_NO_ACCESS_TIP : NO_ACCESS_TIP) : undefined}
        onClick={noAccess ? undefined : () => onSelect(thread)}
      >
        <span className="iter1-row-title">{title}</span>
        {noAccess && <span className="iter1-row-glyph" aria-hidden="true"><Icon name="prohibit" size={16} /></span>}
        {shared && (
          <>
            <span className="iter1-row-glyph" data-tip={shared} aria-hidden="true"><Icon name="users" size={16} /></span>
            <span className="iter1-sr-only">, {shared.charAt(0).toLowerCase() + shared.slice(1)}</span>
          </>
        )}
      </button>
      {/* A participant's no-access row has no menu, except to unpin one they pinned. */}
      {(!noAccess || menuProps.isOwner || thread.pinned) && <QueryMenu thread={thread} {...menuProps} />}
    </li>
  );
}

function Section({ id, heading, threads, activeThreadId, onSelect, menuPropsFor, rowRef }) {
  if (!threads.length) return null;
  return (
    <section className="iter1-section" aria-labelledby={heading ? id : undefined}>
      {heading && <h2 id={id} className="iter1-section-heading">{heading}</h2>}
      <ul className="iter1-list">
        {threads.map((t) => (
          <QueryRow key={t.id} thread={t} active={t.id === activeThreadId} onSelect={onSelect} menuProps={menuPropsFor(t)} rowRef={rowRef} />
        ))}
      </ul>
    </section>
  );
}

/**
 * PRD-1621: the side panel — History and Datasets as tabs (roving tabindex). History lists
 * Pinned, Shared by others, then Queries, newest activity first; searching shows one flat list.
 */
function HistoryPanel({
  threads,
  sharedThreads,
  activeThreadId,
  loading,
  isOwner,
  sharingEnabled,
  onSelect,
  onTogglePin,
  onRename,
  onShare,
  onStopSharing,
  onDelete,
  datasetsTab,
  tab,
  onTabChange,
  rowRef,
}) {
  const [search, setSearch] = useState('');
  const tabRefs = { history: useRef(null), datasets: useRef(null) };

  const { pinned, shared, own } = useMemo(() => {
    const all = [...threads, ...sharedThreads.filter((s) => !threads.some((t) => t.id === s.id))];
    return {
      pinned: all.filter((t) => t.pinned).sort(byActivity),
      shared: sharedThreads.filter((t) => !t.pinned).sort(byActivity),
      own: threads.filter((t) => !t.pinned).sort(byActivity),
    };
  }, [threads, sharedThreads]);

  const query = search.trim().toLowerCase();
  const matches = query
    ? [...pinned, ...shared, ...own].filter((t) => rowLabel(t, isOwner(t)).toLowerCase().includes(query))
    : null;

  const menuPropsFor = (t) => ({
    isOwner: isOwner(t), sharingEnabled, onTogglePin, onRename, onShare, onStopSharing, onDelete,
  });

  const onTabKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = tab === 'history' ? 'datasets' : 'history';
    onTabChange(next);
    tabRefs[next].current?.focus();
  };

  return (
    <div className="iter1-panel">
      <div className="iter1-tabs" role="tablist" aria-label="Queries and datasets" onKeyDown={onTabKey}>
        {['history', 'datasets'].map((key) => (
          <button
            key={key}
            ref={tabRefs[key]}
            type="button"
            role="tab"
            id={`iter1-tab-${key}`}
            aria-controls={`iter1-tabpanel-${key}`}
            aria-selected={tab === key}
            tabIndex={tab === key ? 0 : -1}
            className={`iter1-tab${tab === key ? ' iter1-tab--active' : ''}`}
            data-label={key === 'history' ? 'History' : 'Datasets'}
            onClick={() => onTabChange(key)}
          >
            {key === 'history' ? 'History' : 'Datasets'}
          </button>
        ))}
      </div>

      <div
        id="iter1-tabpanel-history"
        role="tabpanel"
        aria-labelledby="iter1-tab-history"
        hidden={tab !== 'history'}
        className="iter1-tabpanel"
      >
        <div className="iter1-search">
          <Icon name="search" size={16} className="iter1-search-icon" />
          <input
            type="search"
            className="resui-input iter1-search-input"
            placeholder="Search queries..."
            aria-label="Search queries"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" className="iter1-search-clear" aria-label="Clear search" data-tip="Clear search" onClick={() => setSearch('')}>
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
        <div className="iter1-scroll focus-room-list">
          {loading ? (
            <p className="iter1-empty">Loading…</p>
          ) : matches ? (
            matches.length ? (
              <Section id="iter1-results" heading={null} threads={matches} activeThreadId={activeThreadId} onSelect={onSelect} menuPropsFor={menuPropsFor} rowRef={rowRef} />
            ) : (
              <p className="iter1-empty">No queries match your search.</p>
            )
          ) : pinned.length + shared.length + own.length === 0 ? (
            <p className="iter1-empty">No queries yet. Ask a question to start one.</p>
          ) : (
            <>
              <Section id="iter1-pinned" heading="Pinned" threads={pinned} activeThreadId={activeThreadId} onSelect={onSelect} menuPropsFor={menuPropsFor} rowRef={rowRef} />
              <Section id="iter1-shared" heading="Shared by others" threads={shared} activeThreadId={activeThreadId} onSelect={onSelect} menuPropsFor={menuPropsFor} rowRef={rowRef} />
              <Section id="iter1-queries" heading="Queries" threads={own} activeThreadId={activeThreadId} onSelect={onSelect} menuPropsFor={menuPropsFor} rowRef={rowRef} />
            </>
          )}
        </div>
      </div>

      <div
        id="iter1-tabpanel-datasets"
        role="tabpanel"
        aria-labelledby="iter1-tab-datasets"
        hidden={tab !== 'datasets'}
        className="iter1-tabpanel"
      >
        {datasetsTab}
      </div>
    </div>
  );
}

export default HistoryPanel;
